// Issue #52 fatia υ + issue #211 — single source of truth for admin
// route-level role access (frontend redirects).
//
// `routeAccessFor` decides whether a role can REACH a given admin route,
// separate from `can()` which decides whether the backend mutation would
// succeed. Two contracts intentionally because "matrix-user sees admin
// shell but APIs 403" is a documented GAP (υ-E) — UX redirect must follow
// the operator-validated grid even while the API gap stays open.
//
// `adminGuardFor` is the layout-facing wrapper: it accepts the live
// `SessionRole` (underscore form, e.g. "matrix_user") + a raw pathname
// and returns a redirect decision. AdminLayout consumes ONLY this so the
// grid below is the single guard authority (issue #211: kill the old
// email/`matrixOnly` heuristics).

import type { Role } from "./permissionMatrix";

export type AdminRoute =
  | "/admin"
  | "/admin/users"
  | "/admin/plans"
  | "/admin/admins"
  | "/admin/matrix-webhooks"
  | "/admin/voxuy"
  | "/admin/test-mode"
  | "/admin/marketing"
  | "/admin/active-campaign"
  | "/admin/gamification"
  | "/admin/payments"
  | "/admin/refunds"
  | "/admin/integrations"
  | "/admin/logs";

export type RouteOutcome = "render" | "redirect_root" | "redirect_admin_home";

const ROUTE_GRID: Record<AdminRoute, ReadonlySet<Role>> = {
  "/admin": new Set<Role>(["support", "admin", "matrix-user"]),
  "/admin/users": new Set<Role>(["support", "admin"]),
  "/admin/plans": new Set<Role>(["support", "admin"]),
  // #52 λ7: support LISTS admins read-only — `role.read` is in the support
  // privilege set (permissionMatrix.ts). Mutations stay admin-only: the
  // create CTA/delete buttons are gated by `role === "admin"` in AdminsPage
  // and PUT/DELETE /api/v2/admin/roles require role.manage server-side.
  "/admin/admins": new Set<Role>(["support", "admin"]),
  "/admin/matrix-webhooks": new Set<Role>(["admin", "matrix-user"]),
  "/admin/voxuy": new Set<Role>(["admin", "matrix-user"]),
  "/admin/test-mode": new Set<Role>(["admin"]),
  "/admin/marketing": new Set<Role>(["admin"]),
  // ActiveCampaign reads and sync mutations use CurrentAdmin server-side.
  "/admin/active-campaign": new Set<Role>(["admin"]),
  // #229: support VIEWS the gamification surface (spins/ledger/stats readers);
  // edit controls (config/prize/flags/simulate) are admin-only in the UI.
  "/admin/gamification": new Set<Role>(["support", "admin"]),
  "/admin/payments": new Set<Role>(["support", "admin"]),
  // #189 refund portal panel — admin OR support.
  "/admin/refunds": new Set<Role>(["support", "admin"]),
  "/admin/integrations": new Set<Role>(["admin"]),
  "/admin/logs": new Set<Role>(["support", "admin"]),
};

export function routeAccessFor(role: Role, route: AdminRoute): RouteOutcome {
  if (role === "normal") return "redirect_root";
  if (ROUTE_GRID[route].has(role)) return "render";
  return "redirect_admin_home";
}

// --- layout-facing guard (issue #211) ------------------------------------

/** Effective session role string emitted by the backend session payload. */
export type SessionRoleName = "admin" | "support" | "matrix_user" | "user";

/** Where the layout should send the caller (or render in place). */
export type AdminGuardOutcome =
  | "render"
  | "redirect_login"
  | "redirect_root"
  | "redirect_admin_home"
  | "redirect_matrix";

// Most specific prefixes first so `/admin/test-mode/history` maps to
// `/admin/test-mode`, not `/admin`.
const ROUTE_PREFIXES: readonly AdminRoute[] = [
  "/admin/matrix-webhooks",
  "/admin/active-campaign",
  "/admin/integrations",
  "/admin/gamification",
  "/admin/marketing",
  "/admin/test-mode",
  "/admin/payments",
  "/admin/refunds",
  "/admin/plans",
  "/admin/admins",
  "/admin/voxuy",
  "/admin/users",
  "/admin/logs",
  "/admin",
] as const;

function toGridRole(role: SessionRoleName): Role {
  if (role === "matrix_user") return "matrix-user";
  if (role === "admin") return "admin";
  if (role === "support") return "support";
  return "normal";
}

function matchAdminRoute(pathname: string): AdminRoute | null {
  for (const route of ROUTE_PREFIXES) {
    if (pathname === route || pathname.startsWith(`${route}/`)) return route;
  }
  return null;
}

/** Resolve the layout redirect for `role` landing on `pathname`.
 *
 * Example: adminGuardFor("support", "/admin/test-mode") === "redirect_admin_home"
 */
export function adminGuardFor(
  role: SessionRoleName,
  pathname: string,
): AdminGuardOutcome {
  if (role === "user") return "redirect_root";
  if (pathname.startsWith("/matrix")) {
    // The matrix surface is for admin + matrix_user only; support has no
    // matrix access (mirrors the legacy isSupportBlockedPath redirect).
    return role === "support" ? "redirect_admin_home" : "render";
  }
  // Operador 09/07/2026: matrix_user vê APENAS a superfície /matrix —
  // "nenhuma outra página". Os carve-outs de /admin/logs e /admin/matrix-self
  // (#52 υ-E) foram removidos por decisão do operador.
  if (role === "matrix_user") {
    return "redirect_matrix";
  }
  const route = matchAdminRoute(pathname);
  if (route === null) return "render";
  const outcome = routeAccessFor(toGridRole(role), route);
  if (outcome === "render") return "render";
  if (outcome === "redirect_root") return "redirect_root";
  return "redirect_admin_home";
}
